import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import BottomNav from '../BottomNav';
import FavoriteButton from './FavoriteButton';
import { sendPushNotification } from '../sendPushNotification';
import { useTrackEvent } from '../hooks/useAnalytics';
import theme from '../theme';

function resolveDisplayName(author) {
  if (!author) return 'Utente';
  if (!author.includes('@')) return author;
  const namePart = author.split('@')[0];
  return namePart.charAt(0).toUpperCase() + namePart.slice(1);
}

const legacyTimeSlotLabels = {
  mattina: '🌅 Mattina (8:00 - 13:00)',
  pomeriggio: '☀️ Pomeriggio (13:00 - 18:00)',
  sera: '🌙 Sera (18:00 - 22:00)',
  tutto_il_giorno: '🕐 Tutto il giorno (8:00 - 18:00)',
  da_concordare: '💬 Da concordare',
};

function formatTimeSlot(value) {
  if (!value) return null;
  if (legacyTimeSlotLabels[value]) return legacyTimeSlotLabels[value];
  const match = value.match(/^(\d{2}:\d{2})-(\d{2}:\d{2})$/);
  if (match) return `🕐 Dalle ${match[1]} alle ${match[2]}`;
  return value;
}

function CalendarPicker({ availableDates, selectedDates, onToggleDate }) {
  const dates = availableDates || [];
  if (dates.length === 0) return null;
  const months = {};
  dates.forEach(dateStr => {
    const d = new Date(dateStr + 'T00:00:00');
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (!months[key]) months[key] = { year: d.getFullYear(), month: d.getMonth(), availableDays: [] };
    months[key].availableDays.push({ day: d.getDate(), dateStr });
  });
  const monthNames = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
  const dayNames = ['Lun','Mar','Mer','Gio','Ven','Sab','Dom'];
  return (
    <div>
      {Object.values(months).map(({ year, month, availableDays }) => {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDay = new Date(year, month, 1).getDay();
        const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;
        return (
          <div key={`${year}-${month}`} style={{ background: theme.colors.white, borderRadius: theme.radius.lg, border: `1px solid ${theme.colors.border}`, overflow: 'hidden', marginBottom: theme.spacing.md }}>
            <div style={{ background: theme.colors.primary, color: theme.colors.white, padding: '10px 16px', fontWeight: 700, fontSize: theme.font.size.md }}>{monthNames[month]} {year}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '8px 8px 0' }}>
              {dayNames.map(d => <div key={d} style={{ textAlign: 'center', fontSize: theme.font.size.xs, color: theme.colors.textMuted, fontWeight: 600, padding: '4px 0' }}>{d}</div>)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '4px 8px 12px', gap: 4 }}>
              {Array.from({ length: adjustedFirstDay }).map((_, i) => <div key={`e-${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const availableDay = availableDays.find(d => d.day === day);
                const isAvailable = !!availableDay;
                const isSelected = availableDay && selectedDates.includes(availableDay.dateStr);
                return (
                  <div key={day} onClick={() => isAvailable && onToggleDate(availableDay.dateStr)} style={{
                    textAlign: 'center', padding: '8px 4px', borderRadius: theme.radius.sm,
                    fontSize: theme.font.size.base, fontWeight: isSelected ? 700 : 400,
                    background: isSelected ? theme.colors.primary : isAvailable ? theme.colors.primaryLight : 'transparent',
                    color: isSelected ? theme.colors.white : isAvailable ? theme.colors.primary : '#ccc',
                    cursor: isAvailable ? 'pointer' : 'default',
                    border: isAvailable && !isSelected ? '1px solid #a5d6a7' : 'none',
                    transition: 'all 0.15s'
                  }}>{day}</div>
                );
              })}
            </div>
            <div style={{ padding: '4px 16px 10px', fontSize: theme.font.size.xs, color: theme.colors.textMuted }}>🟩 giorni disponibili — clicca per selezionare</div>
          </div>
        );
      })}
    </div>
  );
}

function CalendarView({ selectedDates }) {
  const dates = selectedDates || [];
  if (dates.length === 0) return null;
  const months = {};
  dates.forEach(dateStr => {
    const d = new Date(dateStr + 'T00:00:00');
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (!months[key]) months[key] = { year: d.getFullYear(), month: d.getMonth(), days: [] };
    months[key].days.push(d.getDate());
  });
  const monthNames = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
  const dayNames = ['Lun','Mar','Mer','Gio','Ven','Sab','Dom'];
  return (
    <div>
      {Object.values(months).map(({ year, month, days }) => {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDay = new Date(year, month, 1).getDay();
        const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;
        return (
          <div key={`${year}-${month}`} style={{ background: theme.colors.white, borderRadius: theme.radius.lg, border: `1px solid ${theme.colors.border}`, overflow: 'hidden', marginBottom: theme.spacing.md }}>
            <div style={{ background: theme.colors.primary, color: theme.colors.white, padding: '10px 16px', fontWeight: 700, fontSize: theme.font.size.md }}>{monthNames[month]} {year}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '8px 8px 0' }}>
              {dayNames.map(d => <div key={d} style={{ textAlign: 'center', fontSize: theme.font.size.xs, color: theme.colors.textMuted, fontWeight: 600, padding: '4px 0' }}>{d}</div>)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '4px 8px 12px', gap: 4 }}>
              {Array.from({ length: adjustedFirstDay }).map((_, i) => <div key={`e-${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const isSelected = days.includes(day);
                return (
                  <div key={day} style={{
                    textAlign: 'center', padding: '8px 4px', borderRadius: theme.radius.sm,
                    fontSize: theme.font.size.base, fontWeight: isSelected ? 700 : 400,
                    background: isSelected ? theme.colors.primary : 'transparent',
                    color: isSelected ? theme.colors.white : theme.colors.textPrimary,
                  }}>{day}</div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PhotoGrid({ urls }) {
  const [lightbox, setLightbox] = useState(null);
  if (!urls || urls.length === 0) return null;
  return (
    <div style={{ marginBottom: theme.spacing.xl }}>
      <div style={{ display: 'grid', gridTemplateColumns: urls.length === 1 ? '1fr' : 'repeat(3, 1fr)', gap: 6 }}>
        {urls.map((url, index) => (
          <div key={index} onClick={() => setLightbox(index)} style={{ borderRadius: theme.radius.sm, overflow: 'hidden', aspectRatio: '1', cursor: 'pointer' }}>
            <img src={url} alt={`foto ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        ))}
      </div>
      {lightbox !== null && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <button onClick={(e) => { e.stopPropagation(); setLightbox(l => l > 0 ? l - 1 : urls.length - 1); }} style={{ position: 'absolute', left: 16, background: 'rgba(255,255,255,0.2)', border: 'none', color: theme.colors.white, fontSize: 28, cursor: 'pointer', borderRadius: theme.radius.circle, width: 44, height: 44 }}>‹</button>
          <img src={urls[lightbox]} alt="foto" style={{ maxWidth: '90%', maxHeight: '80vh', objectFit: 'contain', borderRadius: theme.radius.sm }} />
          <button onClick={(e) => { e.stopPropagation(); setLightbox(l => l < urls.length - 1 ? l + 1 : 0); }} style={{ position: 'absolute', right: 16, background: 'rgba(255,255,255,0.2)', border: 'none', color: theme.colors.white, fontSize: 28, cursor: 'pointer', borderRadius: theme.radius.circle, width: 44, height: 44 }}>›</button>
          <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.2)', border: 'none', color: theme.colors.white, fontSize: 20, cursor: 'pointer', borderRadius: theme.radius.circle, width: 36, height: 36 }}>✕</button>
          <div style={{ position: 'absolute', bottom: 20, color: theme.colors.white, fontSize: theme.font.size.base }}>{lightbox + 1} / {urls.length}</div>
        </div>
      )}
    </div>
  );
}

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

async function sendEmailNotification(type, to, data) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const res = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ type, to, data }),
    });
    const json = await res.json();
    console.log('Email response:', json);
  } catch (e) {
    console.error('Errore notifica email:', e);
  }
}

function JobDetail({ session, profile }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const trackedView = useRef(false);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  const [proposedPrice, setProposedPrice] = useState('');
  const [proposedPriceType, setProposedPriceType] = useState('fisso');
  const [proposedHours, setProposedHours] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [authorRating, setAuthorRating] = useState(null);
  const [authorProfile, setAuthorProfile] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [payoutReady, setPayoutReady] = useState(false);
  const [checkingPayout, setCheckingPayout] = useState(true);

  useEffect(() => { fetchJob(); fetchFavoriteStatus(); fetchPayoutStatus(); }, []);

  const fetchFavoriteStatus = async () => {
    const { data } = await supabase
      .from('favorites')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('job_id', id)
      .maybeSingle();
    setIsFavorite(!!data);
  };

  const fetchPayoutStatus = async () => {
    setCheckingPayout(true);
    const { data } = await supabase
      .from('stripe_accounts')
      .select('onboarding_status')
      .eq('user_id', session.user.id)
      .maybeSingle();
    setPayoutReady(data?.onboarding_status === 'completato');
    setCheckingPayout(false);
  };

  const toggleFavorite = async () => {
    if (isFavorite) {
      setIsFavorite(false);
      await supabase.from('favorites').delete().eq('user_id', session.user.id).eq('job_id', id);
    } else {
      setIsFavorite(true);
      await supabase.from('favorites').insert({ user_id: session.user.id, job_id: id });
    }
  };

  const fetchJob = async () => {
    const { data, error } = await supabase.from('jobs').select('*').eq('id', id).single();
    if (!error) {
      setJob(data);

      // Traccia "annuncio_visualizzato" una sola volta per caricamento,
      // solo se il visitatore non è l'autore dell'annuncio (visita reale di un potenziale candidato)
      if (!trackedView.current) {
        const viewerIsOwner = data.author === session?.user?.email || data.author === profile?.full_name;
        if (!viewerIsOwner) {
          trackEvent('annuncio_visualizzato', {
            job_id: data.id,
            category: data.category || null,
            city: data.city || null,
          });
        }
        trackedView.current = true;
      }

      checkAlreadyApplied(data);
      const displayName = resolveDisplayName(data.author);
      fetchAuthorRating(displayName);
      fetchAuthorProfile(displayName);
    }
    setLoading(false);
  };

  const fetchAuthorRating = async (authorName) => {
    const { data } = await supabase.from('reviews').select('stars').eq('reviewed_email', authorName).eq('role', 'committente');
    if (data && data.length > 0) {
      const avg = data.reduce((sum, r) => sum + r.stars, 0) / data.length;
      setAuthorRating({ avg: avg.toFixed(1), count: data.length });
    }
  };

  const fetchAuthorProfile = async (authorName) => {
    const { data } = await supabase.from('profiles').select('id, full_name, avatar_url').eq('full_name', authorName).single();
    if (data) setAuthorProfile(data);
  };

  const checkAlreadyApplied = async (job) => {
    const { data } = await supabase.from('applications').select('id').eq('job_id', job.id).eq('applicant_email', session?.user?.email).single();
    if (data) setAlreadyApplied(true);
  };

  const handleToggleDate = (dateStr) => {
    setSelectedDates(prev => prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr].sort());
  };

  const isOraValid = proposedPriceType !== 'ora' || (proposedHours && parseFloat(proposedHours) > 0);
  const proposedTotal = proposedPriceType === 'ora' && proposedPrice && proposedHours
    ? (parseFloat(proposedPrice) * parseFloat(proposedHours)).toFixed(2)
    : null;

  const handleApply = async () => {
    if (job.available_dates && job.available_dates.length > 0 && selectedDates.length === 0) {
      alert('Seleziona almeno una data disponibile!');
      return;
    }
    if (proposedPrice && !isOraValid) {
      alert('Inserisci il numero di ore concordate!');
      return;
    }
    setSending(true);

    const { data: newApp, error } = await supabase
      .from('applications')
      .insert([{
        job_id: job.id,
        applicant_email: session?.user?.email,
        applicant_id: session?.user?.id,
        message: message.trim() || null,
        selected_dates: selectedDates.length > 0 ? selectedDates : null,
        proposed_price: proposedPrice.trim() || null,
        proposed_price_type: proposedPriceType,
        proposed_hours: proposedPriceType === 'ora' && proposedHours ? parseFloat(proposedHours) : null,
      }])
      .select()
      .single();

    if (error) { alert('Errore: ' + error.message); setSending(false); return; }

    // Traccia "candidatura_inviata" appena la candidatura è stata inserita con successo
    trackEvent('candidatura_inviata', {
      job_id: job.id,
      application_id: newApp.id,
      category: job.category || null,
      proposed_price: proposedPrice.trim() || null,
      proposed_price_type: proposedPriceType,
    });

    const conversationId = `app_${newApp.id}`;
    const applicantName = profile?.full_name || session?.user?.email;
    const priceLabel = proposedTotal
      ? `€${proposedPrice}/h × ${proposedHours}h = €${proposedTotal}`
      : proposedPrice ? `€${proposedPrice}` : null;

    await supabase.from('messages').insert([{
      conversation_id: conversationId,
      sender_id: session.user.id,
      content: `📋 Nuova candidatura per "${job.title}"`,
      read: false,
      type: 'application',
      offer_data: {
        application_id: newApp.id,
        job_title: job.title,
        job_time_slot: job.time_slot || null,
        applicant_name: applicantName,
        applicant_email: session?.user?.email,
        applicant_id: session?.user?.id,
        proposed_price: proposedPrice.trim() || null,
        proposed_price_type: proposedPriceType,
        proposed_hours: proposedPriceType === 'ora' && proposedHours ? parseFloat(proposedHours) : null,
        selected_dates: selectedDates.length > 0 ? selectedDates : null,
        message: message.trim() || null,
      },
    }]);

    const authorDisplayName = resolveDisplayName(job.author);
    const { data: employerEmail } = await supabase
      .rpc('get_email_by_name', { full_name_input: authorDisplayName });
    if (employerEmail) {
      await sendEmailNotification('nuova_candidatura', employerEmail, {
        jobTitle: job.title,
        employerName: authorDisplayName,
        applicantName,
        proposedPrice: priceLabel,
        message: message.trim() || null,
      });
    }

    // Notifica push: nuova candidatura ricevuta
    if (authorProfile?.id) {
      await sendPushNotification({
        userId: authorProfile.id,
        title: '📋 Nuova candidatura ricevuta',
        body: `${applicantName} si è candidato/a per "${job.title}"`,
        url: `/chat/${conversationId}`,
      });
    }

    setSending(false);
    setSent(true);
    setShowForm(false);
    setAlreadyApplied(true);
  };

  if (loading) return <div style={{ padding: theme.spacing.lg, textAlign: 'center', color: theme.colors.textMuted }}>Caricamento...</div>;
  if (!job) return <div style={{ padding: theme.spacing.lg }}>Lavoro non trovato.</div>;

  const displayName = resolveDisplayName(job.author);
  const isOwner = job.author === session?.user?.email || job.author === profile?.full_name;
  const cityIsValid = job.city && isNaN(job.city) && !job.city.includes('/');
  const photos = job.image_urls || (job.image_url ? [job.image_url] : []);
  const identityStatus = profile?.identity_status || 'non_verificato';
  const formattedTimeSlot = formatTimeSlot(job.time_slot);

  return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family }}>
      <div style={{ background: theme.colors.primary, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: theme.colors.white, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <h2 style={{ margin: 0, color: theme.colors.white, fontSize: theme.font.size.xxl, flex: 1 }}>Dettaglio lavoro</h2>
        {!isOwner && (
          <FavoriteButton isFavorite={isFavorite} onToggle={toggleFavorite} />
        )}
      </div>

      <div style={{ padding: theme.spacing.xl, paddingBottom: 80 }}>
        <PhotoGrid urls={photos} />
        <h2 style={{ margin: '0 0 6px', fontSize: theme.font.size.heading }}>{job.title}</h2>
        <div style={{ fontSize: 24, fontWeight: 700, color: theme.colors.primary, marginBottom: theme.spacing.md }}>{job.price}</div>

        <div style={{ display: 'flex', gap: theme.spacing.sm, marginBottom: theme.spacing.xl, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ background: theme.colors.primaryLight, color: theme.colors.primary, padding: '2px 10px', borderRadius: theme.radius.md, fontSize: theme.font.size.sm }}>{job.category}</span>
          {cityIsValid && <span style={{ fontSize: theme.font.size.base, color: theme.colors.textMuted }}>📍 {job.city}</span>}
          {job.people_needed && job.people_needed > 1 && (
            <span style={{ background: theme.colors.infoBg, color: theme.colors.info, padding: '2px 10px', borderRadius: theme.radius.md, fontSize: theme.font.size.sm }}>👥 {job.people_needed} persone</span>
          )}
        </div>

        {job.duration && (
          <div style={{ background: theme.colors.background, borderRadius: theme.radius.md, padding: '10px 14px', marginBottom: theme.spacing.lg }}>
            <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted, marginBottom: 3 }}>⏱️ Durata stimata</div>
            <div style={{ fontSize: theme.font.size.md, color: theme.colors.textPrimary, fontWeight: 600 }}>{job.duration}</div>
          </div>
        )}

        {job.available_dates && job.available_dates.length > 0 && (
          <div style={{ marginBottom: theme.spacing.lg }}>
            <div style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, fontWeight: 600, marginBottom: theme.spacing.sm }}>📅 Giorni disponibili</div>
            <CalendarView selectedDates={job.available_dates} />
          </div>
        )}

        {formattedTimeSlot && (
          <div style={{ background: theme.colors.background, borderRadius: theme.radius.md, padding: '10px 14px', marginBottom: theme.spacing.lg }}>
            <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted, marginBottom: 3 }}>🕐 Fascia oraria</div>
            <div style={{ fontSize: theme.font.size.md, color: theme.colors.textPrimary, fontWeight: 600 }}>{formattedTimeSlot}</div>
          </div>
        )}

        {job.description && (
          <div style={{ background: theme.colors.background, borderRadius: theme.radius.md, padding: theme.spacing.lg, marginBottom: theme.spacing.lg }}>
            <div style={{ fontSize: theme.font.size.base, color: theme.colors.textMuted, marginBottom: theme.spacing.sm }}>📝 Descrizione</div>
            <p style={{ margin: 0, fontSize: theme.font.size.md, lineHeight: 1.6, color: theme.colors.textPrimary }}>{job.description}</p>
          </div>
        )}

        {job.requirements && (
          <div style={{ background: theme.colors.background, borderRadius: theme.radius.md, padding: theme.spacing.lg, marginBottom: theme.spacing.lg }}>
            <div style={{ fontSize: theme.font.size.base, color: theme.colors.textMuted, marginBottom: theme.spacing.sm }}>📋 Richieste</div>
            <p style={{ margin: 0, fontSize: theme.font.size.md, lineHeight: 1.6, color: theme.colors.textPrimary }}>{job.requirements}</p>
          </div>
        )}

        <div onClick={() => authorProfile && navigate(`/utente/${authorProfile.id}`)}
          style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 0', borderTop: '1px solid #eee', borderBottom: '1px solid #eee', marginBottom: theme.spacing.xl, cursor: authorProfile ? 'pointer' : 'default' }}>
          <div style={{ width: 44, height: 44, borderRadius: theme.radius.circle, background: theme.colors.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: theme.colors.primary, fontSize: theme.font.size.md, overflow: 'hidden', flexShrink: 0 }}>
            {authorProfile?.avatar_url ? <img src={authorProfile.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : displayName[0].toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: theme.font.size.lg, color: theme.colors.primary }}>{displayName} →</div>
            {authorRating ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
                <span style={{ color: theme.colors.starGold, fontSize: theme.font.size.md }}>★</span>
                <span style={{ fontWeight: 700, fontSize: theme.font.size.base, color: theme.colors.textPrimary }}>{authorRating.avg}</span>
                <span style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted }}>({authorRating.count} recensioni come committente)</span>
              </div>
            ) : (
              <div style={{ fontSize: theme.font.size.base, color: theme.colors.textMuted }}>Nessuna recensione ancora</div>
            )}
          </div>
        </div>

        {sent && (
          <div style={{ background: theme.colors.primaryLight, color: theme.colors.primary, padding: 14, borderRadius: theme.radius.md, marginBottom: theme.spacing.lg, textAlign: 'center', fontSize: theme.font.size.lg }}>
            🎉 Candidatura inviata! Trovi tutto nella chat.
          </div>
        )}

        {!isOwner && identityStatus !== 'verificato' && (
          <div style={{
            background: identityStatus === 'in_verifica' ? theme.colors.warningBgSoft : theme.colors.errorBg,
            border: `1px solid ${identityStatus === 'in_verifica' ? theme.colors.warningBorder : theme.colors.errorBorder}`,
            borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginBottom: 10, textAlign: 'center'
          }}>
            <div style={{ fontSize: 28, marginBottom: theme.spacing.sm }}>🛡️</div>
            <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: theme.font.size.md, color: theme.colors.textPrimary }}>
              Verifica richiesta per candidarsi
            </p>
            <p style={{ margin: '0 0 12px', fontSize: theme.font.size.base, color: theme.colors.textSecondary, lineHeight: 1.5 }}>
              {identityStatus === 'in_verifica'
                ? 'Il tuo documento è in fase di revisione. Attendi l\'approvazione prima di candidarti.'
                : 'Devi verificare la tua identità prima di poterti candidare a un lavoro.'}
            </p>
            {identityStatus !== 'in_verifica' && (
              <button onClick={() => navigate('/profilo')} style={{
                background: theme.colors.primary, color: theme.colors.white, border: 'none',
                borderRadius: theme.radius.sm, padding: '10px 20px', fontSize: theme.font.size.base,
                fontWeight: 600, cursor: 'pointer'
              }}>Vai alla verifica identità</button>
            )}
          </div>
        )}

        {!isOwner && identityStatus === 'verificato' && !checkingPayout && !payoutReady && (
          <div style={{
            background: theme.colors.warningBgSoft, border: `1px solid ${theme.colors.warningBorder}`,
            borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginBottom: 10, textAlign: 'center'
          }}>
            <div style={{ fontSize: 28, marginBottom: theme.spacing.sm }}>💼</div>
            <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: theme.font.size.md, color: theme.colors.textPrimary }}>
              Configurazione richiesta per candidarsi
            </p>
            <p style={{ margin: '0 0 12px', fontSize: theme.font.size.base, color: theme.colors.textSecondary, lineHeight: 1.5 }}>
              Devi configurare il tuo account per ricevere pagamenti prima di poterti candidare a un lavoro.
            </p>
            <button onClick={() => navigate('/pagamenti')} style={{
              background: theme.colors.primary, color: theme.colors.white, border: 'none',
              borderRadius: theme.radius.sm, padding: '10px 20px', fontSize: theme.font.size.base,
              fontWeight: 600, cursor: 'pointer'
            }}>Configura per ricevere pagamenti</button>
          </div>
        )}

        {!isOwner && identityStatus === 'verificato' && payoutReady && !alreadyApplied && !showForm && (
          <button onClick={() => setShowForm(true)} style={{ width: '100%', background: theme.colors.primary, color: theme.colors.white, border: 'none', borderRadius: theme.radius.lg, padding: 14, fontSize: theme.font.size.xl, cursor: 'pointer', marginBottom: 10 }}>
            💬 Candidati per questo lavoro
          </button>
        )}

        {!isOwner && identityStatus === 'verificato' && payoutReady && alreadyApplied && !sent && (
          <div style={{ background: theme.colors.backgroundAlt, color: theme.colors.textSecondary, padding: 14, borderRadius: theme.radius.lg, textAlign: 'center', marginBottom: 10 }}>
            ✅ Hai già inviato una candidatura per questo lavoro
          </div>
        )}

        {isOwner && (
          <div style={{ background: theme.colors.primaryLight, color: theme.colors.primary, padding: 14, borderRadius: theme.radius.lg, textAlign: 'center', marginBottom: 10, fontSize: theme.font.size.md }}>
            📋 Questo è un tuo annuncio — le candidature arrivano direttamente in chat
          </div>
        )}

        {showForm && (
          <div style={{ background: theme.colors.background, borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginBottom: theme.spacing.lg }}>
            <div style={{ fontSize: theme.font.size.md, fontWeight: 700, marginBottom: theme.spacing.lg, color: theme.colors.textPrimary }}>La tua candidatura</div>

            {job.available_dates && job.available_dates.length > 0 && (
              <div style={{ marginBottom: theme.spacing.lg }}>
                <div style={{ fontSize: theme.font.size.base, fontWeight: 600, color: '#444', marginBottom: theme.spacing.sm }}>📅 Seleziona i giorni in cui sei disponibile *</div>
                <CalendarPicker availableDates={job.available_dates} selectedDates={selectedDates} onToggleDate={handleToggleDate} />
                {selectedDates.length > 0 && (
                  <div style={{ fontSize: theme.font.size.sm, color: theme.colors.primary, marginTop: 6 }}>
                    ✅ {selectedDates.length} {selectedDates.length === 1 ? 'giorno selezionato' : 'giorni selezionati'}
                  </div>
                )}
              </div>
            )}

            {formattedTimeSlot && (
              <div style={{ background: theme.colors.primaryLight, borderRadius: theme.radius.md, padding: '10px 14px', marginBottom: theme.spacing.lg }}>
                <div style={{ fontSize: theme.font.size.xs, color: theme.colors.primary, marginBottom: 3, fontWeight: 600 }}>🕐 Fascia oraria richiesta</div>
                <div style={{ fontSize: theme.font.size.md, color: theme.colors.textPrimary, fontWeight: 600 }}>{formattedTimeSlot}</div>
              </div>
            )}

            <div style={{ marginBottom: theme.spacing.lg }}>
              <div style={{ fontSize: theme.font.size.base, fontWeight: 600, color: '#444', marginBottom: 6 }}>💰 Il tuo prezzo</div>
              <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, marginBottom: theme.spacing.sm }}>Prezzo richiesto: <strong>{job.price}</strong> — puoi proporre un prezzo diverso</div>
              <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 90 }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: theme.colors.primary, fontWeight: 700, fontSize: theme.font.size.xl }}>€</span>
                  <input type="number" value={proposedPrice} onChange={e => setProposedPrice(e.target.value)}
                    placeholder={job.price?.replace('€', '').replace('/h', '').replace(' fisso', '').trim()} min="0" step="0.01"
                    style={{ width: '100%', padding: '10px 12px 10px 28px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }} />
                </div>
                <select value={proposedPriceType} onChange={e => setProposedPriceType(e.target.value)}
                  style={{ padding: '10px 10px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, background: theme.colors.white }}>
                  <option value="fisso">fisso</option>
                  <option value="ora">/ ora</option>
                </select>
                {proposedPriceType === 'ora' && (
                  <input type="number" value={proposedHours} onChange={e => setProposedHours(e.target.value)}
                    placeholder="Ore" min="0.5" step="0.5"
                    style={{ width: 70, padding: '10px 10px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }} />
                )}
              </div>
              {proposedTotal && (
                <div style={{ marginTop: 8, fontSize: theme.font.size.base, color: theme.colors.primary, fontWeight: 600 }}>
                  Totale proposto: €{proposedPrice} × {proposedHours}h = €{proposedTotal}
                </div>
              )}
            </div>

            <div style={{ marginBottom: theme.spacing.lg }}>
              <div style={{ fontSize: theme.font.size.base, fontWeight: 600, color: '#444', marginBottom: 6 }}>💬 Messaggio <span style={{ fontWeight: 400, color: theme.colors.textMuted }}>(facoltativo)</span></div>
              <textarea value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Presentati e spiega perché sei la persona giusta..."
                rows={3} style={{ width: '100%', padding: '10px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box', resize: 'none' }} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowForm(false)} style={{ flex: 1, background: theme.colors.white, color: theme.colors.textSecondary, border: `1px solid ${theme.colors.borderInput}`, borderRadius: theme.radius.md, padding: 12, fontSize: theme.font.size.md, cursor: 'pointer' }}>Annulla</button>
              <button onClick={handleApply} disabled={sending || (proposedPriceType === 'ora' && proposedPrice && !proposedHours)} style={{ flex: 2, background: sending ? theme.colors.textMuted : theme.colors.primary, color: theme.colors.white, border: 'none', borderRadius: theme.radius.md, padding: 12, fontSize: theme.font.size.md, cursor: sending ? 'not-allowed' : 'pointer' }}>
                {sending ? 'Invio...' : 'Invia candidatura'}
              </button>
            </div>
          </div>
        )}

        <button onClick={() => navigate('/')} style={{ width: '100%', background: theme.colors.white, color: theme.colors.primary, border: `1px solid ${theme.colors.primary}`, borderRadius: theme.radius.lg, padding: 14, fontSize: theme.font.size.xl, cursor: 'pointer' }}>
          ← Torna alla Home
        </button>
      </div>
      <BottomNav />
    </div>
  );
}

export default JobDetail;